import { controlAnswerKey } from "../../utils/regulatoryIdentity"
import { categoryGroups } from "../../utils/regulatoryCategories"
import { QuestionnaireControlCard } from "./QuestionnaireControlCard"
import { answerStatuses } from "../../utils/regulatoryAnswers"
import { Alert, Button, Card, Input, Progress, Select, Space, Tag, Typography } from "antd"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Prompt } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import {
  RegulatoryControlAnswer,
  RegulatoryControlAnswerStatus,
} from "../../generated/proto-ts/main"
import { ICloudRegulatoryControlPoint, IEN1176AnswerMap, calculateEN1176Stats } from "../../types"
import { controlGroup } from "../../utils/regulatoryPresentation"
import { uuidToPbUUID } from "../../utils/utils"
import { AnswerSaveState } from "./useRegulatoryAnswers"
export interface IEN1176QuestionnaireWidgetProps {
  guardNavigation?: boolean
  scopeKey?: string
  controlPoints: ICloudRegulatoryControlPoint[]
  answers: IEN1176AnswerMap
  onAnswersChange: (answer: RegulatoryControlAnswer, options?: { debounce?: boolean }) => void
  onAnswerBlur?: (point: string) => void
  readOnly?: boolean
  title?: React.ReactNode
  saveStates?: Record<string, AnswerSaveState>
  retry?: (uuid: string) => void
  loading?: boolean
  error?: string
  reload?: () => void
  connected?: boolean
}
export const controlPointIDRender = (id: string) => id
const statuses = answerStatuses
export const EN1176QuestionnaireWidget: FC<IEN1176QuestionnaireWidgetProps> = ({
  controlPoints,
  scopeKey = "mission",
  guardNavigation = true,
  answers,
  onAnswersChange,
  onAnswerBlur,
  readOnly,
  title,
  saveStates = {},
  retry,
  loading,
  error,
  reload,
  connected = true,
}) => {
  const [collapsed, setCollapsed] = useState<string[]>([])
  const [expansion, setExpansion] = useState<{ sequence: number; expanded: boolean }>()
  const [filter, setFilter] = useState("all")
  const [section, setSection] = useState("all")
  const [query, setQuery] = useState("")
  const ref = useRef(answers)
  ref.current = answers
  const contextKey = controlPoints
    .map((cp) => `${controlAnswerKey(cp)}/${cp.PointRevisionUUID}`)
    .join(",")
  useEffect(() => {
    setCollapsed([])
    setSection("all")
    setFilter("all")
    setQuery("")
  }, [contextKey])
  const update = useCallback(
    (
      cp: ICloudRegulatoryControlPoint,
      patch: Partial<RegulatoryControlAnswer>,
      debounce = false,
    ) => {
      if (readOnly || loading) return
      const answer =
        ref.current[controlAnswerKey(cp)]?.clone() ??
        new RegulatoryControlAnswer({
          uuid: uuidToPbUUID(uuidv4()),
          control_point_uuid: uuidToPbUUID(cp.UUID),
          context_uuid: cp.AnswerContextUUID ? uuidToPbUUID(cp.AnswerContextUUID) : undefined,
          control_point_revision_uuid: cp.PointRevisionUUID
            ? uuidToPbUUID(cp.PointRevisionUUID)
            : undefined,
          status: RegulatoryControlAnswerStatus.UNKNOWN_STATUS,
          comment: "",
          pictures: [],
          created_at: Date.now(),
        })
      Object.assign(answer, patch)
      answer.updated_at = Date.now()
      ref.current = { ...ref.current, [controlAnswerKey(cp)]: answer }
      onAnswersChange(answer, { debounce })
    },
    [onAnswersChange, readOnly, loading],
  )
  const stats = useMemo(
    () => calculateEN1176Stats(controlPoints.map(controlAnswerKey), answers),
    [controlPoints, answers],
  )
  const groups = useMemo(
    () => Array.from(new Set(controlPoints.map(controlGroup))),
    [controlPoints],
  )
  const searchText = useMemo(
    () =>
      new Map(
        controlPoints.map((cp) => [
          cp.UUID,
          JSON.stringify(cp.Definition ?? cp.Text).toLocaleLowerCase(),
        ]),
      ),
    [controlPoints],
  )
  const filtered = controlPoints.filter((cp) => {
    const status =
      answers[controlAnswerKey(cp)]?.status ?? RegulatoryControlAnswerStatus.UNKNOWN_STATUS
    return (
      (filter === "all" || String(status) === filter) &&
      (section === "all" || section === controlGroup(cp)) &&
      (!query || searchText.get(cp.UUID)?.includes(query.toLocaleLowerCase()))
    )
  })
  const dirty = Object.values(saveStates).some((s) => s.state !== "saved")
  const activeIds = new Set(controlPoints.map(controlAnswerKey))
  const retired = Object.keys(answers).filter((uuid) => !activeIds.has(uuid)).length
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Prompt
        when={guardNavigation && dirty}
        message="Certaines réponses ne sont pas confirmées par le serveur. Quitter cette page abandonnera les modifications non enregistrées. Continuer ?"
      />
      <Card title={title}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space wrap>
            <Tag>
              {stats.answered}/{stats.total} répondus
            </Tag>
            <Tag color={stats.isCompliant ? "green" : "orange"}>
              {stats.total === 0
                ? "Aucun contrôle"
                : stats.answered === 0
                  ? "Non démarré"
                  : stats.answered < stats.total
                    ? "Incomplet"
                    : stats.isCompliant
                      ? "Contrôles renseignés sans anomalie déclarée"
                      : "Contrôles à examiner"}
            </Tag>
            <Tag color="red">{stats.nonConformeCount} non conformes</Tag>
            <Tag>{stats.aControlerCount} à contrôler</Tag>
            <Tag>{stats.naCount} N/A</Tag>
            <Tag color="blue">{stats.observationCount} observations</Tag>
            <Tag color="orange">{stats.notVerifiedCount} non vérifiés</Tag>
          </Space>
          <Progress percent={stats.percentCompleted} />
          <Space wrap>
            <Input
              aria-label="Rechercher un contrôle"
              placeholder="Rechercher un contrôle…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Select
              aria-label="Filtrer les résultats"
              value={filter}
              onChange={setFilter}
              options={[
                { label: "Tous les résultats", value: "all" },
                ...statuses.map((s) => ({ label: s.label, value: String(s.value) })),
              ]}
              style={{ minWidth: 190 }}
            />
            <Select
              aria-label="Filtrer les modules et sections"
              value={section}
              onChange={setSection}
              options={[
                { label: "Tous les modules et sections", value: "all" },
                ...groups.map((g) => ({ label: g, value: g })),
              ]}
              style={{ minWidth: 240, maxWidth: "100%" }}
            />
          </Space>
        </Space>
      </Card>
      {!connected && (
        <Alert
          showIcon
          type="warning"
          message="Cloud déconnecté"
          description="Les modifications restent sur cette page jusqu’à confirmation. Une connexion BLE seule ne permet pas d’enregistrer ces réponses."
        />
      )}
      {error && (
        <Alert
          showIcon
          type="error"
          message={error}
          action={<Button onClick={reload}>Réessayer le chargement</Button>}
        />
      )}
      {loading && <Alert type="info" message="Chargement des réponses…" />}
      {retired > 0 && (
        <Alert
          type="warning"
          message={`${retired} réponse(s) hors du questionnaire actuel, exclue(s) des totaux.`}
        />
      )}
      {!loading && !filtered.length && <Alert type="info" message="Aucun contrôle à afficher." />}
      {controlPoints.some((p) => p.ClassificationReviewed === false) && (
        <Alert
          type="info"
          message="Classement opérationnel proposé ou non revu"
          description="Les catégories organisent la lecture ; elles ne constituent pas une validation des exigences."
        />
      )}
      <Space wrap>
        <Button
          onClick={() => {
            setCollapsed([])
            setExpansion((e) => ({ sequence: (e?.sequence ?? 0) + 1, expanded: true }))
          }}
        >
          Développer les réponses
        </Button>
        <Button
          onClick={() =>
            setExpansion((e) => ({ sequence: (e?.sequence ?? 0) + 1, expanded: false }))
          }
        >
          Réduire les réponses
        </Button>
      </Space>
      <div style={{ width: "100%" }}>
        {categoryGroups(filtered).map((group) => {
          const code = group.category.code,
            closed = collapsed.includes(code)
          const count = calculateEN1176Stats(group.points.map(controlAnswerKey), answers)
          const pending = group.points.filter(
            (p) => saveStates[controlAnswerKey(p)]?.state === "pending",
          ).length
          const failed = group.points.filter(
            (p) => saveStates[controlAnswerKey(p)]?.state === "failed",
          ).length
          return (
            <section className="reg-category-group" key={code}>
              <button
                type="button"
                className="reg-category-heading"
                aria-expanded={!closed}
                aria-controls={`category-${scopeKey}-${code}`}
                onClick={() => {
                  if (!closed) group.points.forEach((p) => onAnswerBlur?.(controlAnswerKey(p)))
                  setCollapsed((c) => (closed ? c.filter((k) => k !== code) : [...c, code]))
                }}
              >
                <img src={`/icones/${group.category.iconKey}.png`} alt="" />
                <strong>{group.category.label}</strong>
                <span>
                  {count.answered}/{count.total} répondus
                </span>
                {count.nonConformeCount > 0 && <Tag color="red">{count.nonConformeCount} NC</Tag>}
                {pending > 0 && <Tag color="blue">{pending} enregistrement(s)</Tag>}
                {failed > 0 && <Tag color="red">{failed} échec(s)</Tag>}
                <span aria-hidden="true">{closed ? "＋" : "−"}</span>
              </button>
              <div id={`category-${scopeKey}-${code}`} hidden={closed}>
                {group.points.map((cp) => (
                  <QuestionnaireControlCard
                    key={`${controlAnswerKey(cp)}/${cp.PointRevisionUUID ?? "legacy"}`}
                    cp={cp}
                    answer={answers[controlAnswerKey(cp)]}
                    state={saveStates[controlAnswerKey(cp)]}
                    readOnly={readOnly}
                    loading={loading}
                    error={error}
                    onUpdate={update}
                    onFlush={onAnswerBlur}
                    retry={retry}
                    expansion={expansion}
                  />
                ))}
              </div>
            </section>
          )
        })}
      </div>
    </Space>
  )
}
